import { useState, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { getRoomConfig } from '../config/roomTypes';
import { useSimpleTextures } from '../../../../hooks/useSimpleTextures';

// Simplified room hook - no texture loading complexity
export const useRoom = (roomType) => {
    const [roomConfig, setRoomConfig] = useState(null);
    const [error, setError] = useState(null);
    
    useEffect(() => {
        try {
            const config = getRoomConfig(roomType);
            setRoomConfig(config);
        } catch (error) {
            console.error(`Error loading room ${roomType}:`, error);
            setError(error);
        }
    }, [roomType]);

    // Load textures if configured
    const { textures, loading: texturesLoading } = useSimpleTextures(roomConfig?.textures);

    // Material creation with textures
    const createWallMaterial = useMemo(() => {
        return (materialConfig = roomConfig?.materials?.walls) => {
            if (!materialConfig) {
                return new THREE.MeshStandardMaterial({ 
                    color: '#ffffff',
                    roughness: 0.8,
                    metalness: 0
                });
            }

            const materialProps = {
                color: materialConfig.color || '#ffffff',
                roughness: materialConfig.roughness || 0.8,
                metalness: materialConfig.metalness || 0
            };

            // Add wall textures if available
            if (textures?.walls) {
                if (textures.walls.color) materialProps.map = textures.walls.color;
                if (textures.walls.normal) materialProps.normalMap = textures.walls.normal;
                if (textures.walls.roughness) materialProps.roughnessMap = textures.walls.roughness;
            }

            return new THREE.MeshStandardMaterial(materialProps);
        };
    }, [roomConfig, textures]);

    const createFloorMaterial = useMemo(() => {
        return (materialConfig = roomConfig?.materials?.floor) => {
            if (!materialConfig) {
                return new THREE.MeshStandardMaterial({ 
                    color: '#f5f5f5',
                    roughness: 0.99, // Almost completely matte
                    metalness: 0.0, // No metalness at all
                    envMapIntensity: 0 // No environment map reflections at all
                });
            }

            const materialProps = {
                color: materialConfig.color || '#f5f5f5',
                roughness: materialConfig.roughness || 0.99, // Almost completely matte
                metalness: materialConfig.metalness || 0.0, // No metalness at all
                envMapIntensity: 0 // No environment map reflections at all
            };

            // Add floor textures if available
            if (textures?.floor) {
                if (textures.floor.color) materialProps.map = textures.floor.color;
                if (textures.floor.normal) materialProps.normalMap = textures.floor.normal;
                if (textures.floor.roughness) materialProps.roughnessMap = textures.floor.roughness;
            }

            return new THREE.MeshStandardMaterial(materialProps);
        };
    }, [roomConfig, textures]);

    return {
        roomConfig,
        createWallMaterial,
        createFloorMaterial,
        texturesLoading,
        error
    };
}; 